import React, { useEffect, useRef, useState } from "react";
import { auth } from "../../../firebaseConfig";

import { fetchMyTutorDoc, updateMyTutorDoc } from "../tutorFirestore";
import { normalizeStatus } from "../tutorStyles";

/* ──────────────────────────────────────────────────────────────────────────
   Styles — modeled on the Admin Employee Detail page (employeeStyles.js)
   so the tutor profile feels consistent with the rest of the app.
─────────────────────────────────────────────────────────────────────────── */
const statusConfig = {
  Active:     { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  "On Hold":  { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  Finished:   { bg: "#f8fafc", color: "#475569", border: "#cbd5e1" },
};
function getStatusCfg(label) {
  return statusConfig[label] || statusConfig.Active;
}

const S = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#fafafa",
  },
  topBar: {
    padding: "14px 24px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    borderBottom: "1px solid #e5e7eb",
    background: "#fff",
    flexShrink: 0,
    flexWrap: "wrap",
  },
  headerInfo: { flex: 1, minWidth: 160 },
  name: { fontSize: 17, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.2px", margin: 0 },
  statusBadge: (label) => {
    const cfg = getStatusCfg(label);
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 12,
      fontWeight: 500,
      color: cfg.color,
      background: cfg.bg,
      border: `1px solid ${cfg.border}`,
      borderRadius: 6,
      padding: "3px 8px",
      whiteSpace: "nowrap",
      marginTop: 5,
    };
  },
  statusDot: (label) => {
    const cfg = getStatusCfg(label);
    return { width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 };
  },
  saveBtn: {
    border: "none",
    borderRadius: 9,
    padding: "9px 20px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    background: "#0f172a",
    color: "#fff",
    flexShrink: 0,
  },
  saveBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },

  scroll: {
    flex: 1,
    overflow: "auto",
    padding: "24px",
    boxSizing: "border-box",
  },
  columns: {
    display: "flex",
    flexWrap: "wrap",
    gap: 18,
    maxWidth: 1040,
  },
  column: {
    flex: "1 1 360px",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },

  sectionCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    overflow: "hidden",
  },
  sectionHeader: { padding: "13px 20px 11px", borderBottom: "1px solid #f1f5f9" },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.7px",
  },

  fieldRow: {
    display: "flex",
    alignItems: "center",
    padding: "11px 20px",
    gap: 16,
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 13,
    color: "#6b7280",
    fontWeight: 400,
    width: 140,
    flexShrink: 0,
  },
  fieldInput: {
    flex: 1,
    minWidth: 0,
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "7px 11px",
    fontSize: 14,
    color: "#0f172a",
    outline: "none",
    background: "#fff",
    fontWeight: 400,
    boxSizing: "border-box",
    width: "100%",
  },
  fieldInputDate: {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "7px 11px",
    fontSize: 14,
    color: "#0f172a",
    outline: "none",
    background: "#fff",
    fontWeight: 400,
    boxSizing: "border-box",
    width: "100%",
    maxWidth: 200,
  },
  // Read-only field value (non-editable tutor fields)
  readonlyValue: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    color: "#0f172a",
    fontWeight: 400,
    padding: "7px 0",
  },
  readonlyMuted: {
    color: "#94a3b8",
  },

  // Notes
  notesCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    overflow: "hidden",
    maxWidth: 1040,
    marginTop: 18,
  },
  notesTextarea: {
    width: "100%",
    border: "none",
    outline: "none",
    resize: "none",
    background: "transparent",
    fontSize: 14,
    color: "#0f172a",
    lineHeight: 1.6,
    padding: "14px 20px",
    boxSizing: "border-box",
    minHeight: 120,
    fontFamily: "inherit",
  },
};

/* ── Small layout helpers ── */
function Section({ title, children }) {
  return (
    <div style={S.sectionCard}>
      <div style={S.sectionHeader}>
        <div style={S.sectionTitle}>{title}</div>
      </div>
      <div style={{ padding: "2px 0" }}>{children}</div>
    </div>
  );
}

function FieldRow({ label, children, last }) {
  return (
    <div style={{ ...S.fieldRow, borderBottom: last ? "none" : "1px solid #f8fafc" }}>
      <div style={S.fieldLabel}>{label}</div>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

function ReadonlyValue({ value }) {
  const has = value !== "" && value != null;
  return (
    <div style={{ ...S.readonlyValue, ...(has ? {} : S.readonlyMuted) }}>
      {has ? value : "—"}
    </div>
  );
}

export default function TutorMyInfo() {
  const uid = auth.currentUser?.uid;

  const [loading, setLoading] = useState(true);
  const [emp, setEmp] = useState(null);
  const [saving, setSaving] = useState(false);

  const noteRef = useRef(null);

  useEffect(() => {
    if (!uid) return;
    (async () => {
      setLoading(true);
      const data = await fetchMyTutorDoc(uid);
      setEmp(data);
      setLoading(false);
    })();
  }, [uid]);

  useEffect(() => {
    if (!noteRef.current) return;
    noteRef.current.style.height = "auto";
    noteRef.current.style.height = `${noteRef.current.scrollHeight}px`;
  }, [emp?.note]);

  const statusLabel = normalizeStatus(emp?.status);

  const setField = (k, v) => setEmp((p) => ({ ...(p || {}), [k]: v }));

  async function handleSave() {
    if (!uid || !emp) return;
    setSaving(true);
    try {
      // tutor can edit these fields
      await updateMyTutorDoc(uid, {
        phone: emp.phone || "",
        homeAddress: emp.homeAddress || "",
        dob: emp.dob || "",
        startWorkingDate: emp.startWorkingDate || "",
        endWorkingDate: emp.endWorkingDate || "",
        note: emp.note || "",
      });
      alert("Saved.");
    } catch (e) {
      console.error(e);
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 28, color: "#94a3b8", fontSize: 14 }}>Loading…</div>;
  }

  if (!emp) {
    return <div style={{ padding: 28, color: "#b91c1c", fontSize: 14 }}>Profile not found.</div>;
  }

  return (
    <div style={S.page}>
      {/* ── Top bar ── */}
      <div style={S.topBar}>
        <div style={S.headerInfo}>
          <div style={S.name}>{emp.name || "My Profile"}</div>
          <span style={{ ...S.statusBadge(statusLabel), display: "inline-flex" }}>
            <span style={S.statusDot(statusLabel)} />
            {statusLabel}
          </span>
        </div>

        <button
          style={{ ...S.saveBtn, ...(saving ? S.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div style={S.scroll}>
        <div style={S.columns}>

          {/* LEFT column */}
          <div style={S.column}>
            <Section title="Account Information">
              <FieldRow label="Full Name">
                <ReadonlyValue value={emp.name} />
              </FieldRow>
              <FieldRow label="Email" last>
                <ReadonlyValue value={emp.email} />
              </FieldRow>
            </Section>

            <Section title="Personal Information">
              <FieldRow label="Phone">
                <input
                  style={S.fieldInput}
                  value={emp.phone || ""}
                  onChange={(e) => setField("phone", e.target.value)}
                  placeholder="(555) 000-0000"
                />
              </FieldRow>
              <FieldRow label="Date of Birth">
                <input
                  type="date"
                  style={S.fieldInputDate}
                  value={emp.dob || ""}
                  onChange={(e) => setField("dob", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Home Address" last>
                <input
                  style={S.fieldInput}
                  value={emp.homeAddress || ""}
                  onChange={(e) => setField("homeAddress", e.target.value)}
                  placeholder="Street, City, State"
                />
              </FieldRow>
            </Section>
          </div>

          {/* RIGHT column */}
          <div style={S.column}>
            <Section title="Employment">
              <FieldRow label="Start Date">
                <input
                  type="date"
                  style={S.fieldInputDate}
                  value={emp.startWorkingDate || ""}
                  onChange={(e) => setField("startWorkingDate", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="End Date">
                <input
                  type="date"
                  style={S.fieldInputDate}
                  value={emp.endWorkingDate || ""}
                  onChange={(e) => setField("endWorkingDate", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Status" last>
                <ReadonlyValue value={statusLabel} />
              </FieldRow>
            </Section>

            <Section title="Compensation">
              <FieldRow label="Teaching Rate">
                <ReadonlyValue
                  value={emp.teachingRate != null && emp.teachingRate !== "" ? `$${emp.teachingRate}/hr` : ""}
                />
              </FieldRow>
              <FieldRow label="Admin Rate" last>
                <ReadonlyValue
                  value={emp.adminRate != null && emp.adminRate !== "" ? `$${emp.adminRate}/hr` : ""}
                />
              </FieldRow>
            </Section>
          </div>
        </div>

        {/* Notes — full width */}
        <div style={S.notesCard}>
          <div style={S.sectionHeader}>
            <div style={S.sectionTitle}>Note</div>
          </div>
          <textarea
            ref={noteRef}
            value={emp.note || ""}
            placeholder="Write notes here…"
            onChange={(e) => setField("note", e.target.value)}
            onInput={() => {
              if (!noteRef.current) return;
              noteRef.current.style.height = "auto";
              noteRef.current.style.height = `${noteRef.current.scrollHeight}px`;
            }}
            style={S.notesTextarea}
            rows={4}
          />
        </div>
      </div>
    </div>
  );
}
